import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from './ConfirmDialog'

export interface ConfirmOptions {
  title?: string
  description?: string
  confirmLabel?: string
  variant?: 'danger' | 'primary'
}

type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * App-wide confirmation gate. Every destructive (delete/remove) action and every
 * update to an existing record must go through this before it fires.
 *
 *   const confirm = useConfirm()
 *   if (!(await confirm({ description: 'Delete this banner?' }))) return
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  // Resolver for the promise handed back to the caller; settled on confirm/cancel.
  const resolveRef = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((o = {}) => {
    setOpts(o)
    return new Promise<boolean>(resolve => { resolveRef.current = resolve })
  }, [])

  const settle = (ok: boolean) => {
    setOpts(null)
    resolveRef.current?.(ok)
    resolveRef.current = null
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!opts}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={opts?.title ?? t('common.confirmTitle')}
        description={opts?.description ?? t('common.confirmDescription')}
        confirmLabel={opts?.confirmLabel}
        variant={opts?.variant ?? 'danger'}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}
